/**
 * Configuration utilities for BWS Market Maker website
 * Handles both local development (LocalStack) and production environments
 */

export interface AppConfig {
  environment: 'local' | 'development' | 'staging' | 'production';
  api: {
    baseUrl: string;
    timeout: number;
    retries: number;
  };
  aws: {
    region: string;
    accessKeyId?: string;
    secretAccessKey?: string;
    dynamoDbEndpoint?: string;
  };
  localstack: {
    enabled: boolean;
    port: number;
    endpoint: string;
    instance: string;
  };
  blockchain: {
    rpcUrl: string;
    chainId: number;
    contracts: {
      bwsToken: string;
      treasury: string;
      vault: string;
      auditTrail: string;
    };
  };
  tables: {
    prefix: string;
    tradingHistory: string;
    marketData: string;
    revenueRecords: string;
    strategyParameters: string;
    performanceMetrics: string;
    threatIntelligence: string;
    systemConfiguration: string;
  };
  features: {
    realTimeUpdates: boolean;
    advancedCharts: boolean;
    logStreaming: boolean;
    exportData: boolean;
    mockDataFallback: boolean;
  };
  debug: {
    apiCalls: boolean;
    showDevTools: boolean;
  };
  intervals: {
    fast: number;    // Real-time data refresh
    medium: number;  // Metrics refresh
    slow: number;    // Background data refresh
  };
}

// Get environment variable with fallback
const getEnvVar = (key: string, fallback: string = ''): string => {
  return process.env[key] || fallback;
};

// Get boolean environment variable
const getEnvBool = (key: string, fallback: boolean = false): boolean => {
  const value = process.env[key];
  if (!value) return fallback;
  return value.toLowerCase() === 'true';
};

// Get number environment variable
const getEnvNumber = (key: string, fallback: number): number => {
  const value = process.env[key];
  if (!value) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
};

// Detect if running in LocalStack mode
export const isLocalStackMode = (): boolean => {
  return getEnvVar('REACT_APP_ENVIRONMENT') === 'local' || 
         getEnvBool('REACT_APP_LOCALSTACK_ENABLED');
};

// Create configuration object
export const createConfig = (): AppConfig => {
  const environment = getEnvVar('REACT_APP_ENVIRONMENT', 'local') as AppConfig['environment'];
  const isLocalStack = environment === 'local';
  const localStackPort = getEnvNumber('REACT_APP_LOCALSTACK_PORT', 4566);
  const localStackInstance = getEnvVar('REACT_APP_BWS_LOCALSTACK_INSTANCE', 'main');
  const tablePrefix = getEnvVar('REACT_APP_TABLE_PREFIX', `bws-market-maker-${localStackInstance}`);

  return {
    environment,
    
    api: {
      baseUrl: isLocalStack
        ? `http://localhost:${localStackPort}`
        : getEnvVar('REACT_APP_API_BASE_URL') || 'https://xyram4ufyy74oybzrcktt6untu0rcyer.lambda-url.us-east-1.on.aws',
      timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 30000),
      retries: getEnvNumber('REACT_APP_API_RETRIES', 3),
    },

    aws: {
      region: getEnvVar('REACT_APP_AWS_REGION', 'us-east-1'),
      accessKeyId: isLocalStack ? getEnvVar('REACT_APP_AWS_ACCESS_KEY_ID', 'test-bws-market-maker') : undefined,
      secretAccessKey: isLocalStack ? getEnvVar('REACT_APP_AWS_SECRET_ACCESS_KEY', 'test-bws-market-maker') : undefined,
      dynamoDbEndpoint: isLocalStack ? `http://localhost:${localStackPort}` : undefined,
    },

    localstack: {
      enabled: isLocalStack,
      port: localStackPort,
      endpoint: getEnvVar('REACT_APP_LOCALSTACK_ENDPOINT', `http://localhost:${localStackPort}`),
      instance: localStackInstance,
    },

    blockchain: {
      rpcUrl: environment === 'production' || environment === 'staging'
        ? getEnvVar('REACT_APP_ETH_RPC_URL', 'https://mainnet.infura.io/v3/')
        : getEnvVar('REACT_APP_ETH_RPC_URL', 'http://127.0.0.1:8545'),
      chainId: environment === 'production' || environment === 'staging'
        ? 1  // Ethereum mainnet
        : getEnvNumber('REACT_APP_CHAIN_ID', 31337),
      contracts: {
        bwsToken: getEnvVar('REACT_APP_BWS_TOKEN_ADDRESS',
          environment === 'production' || environment === 'staging'
            ? '0xaade1d6701173bd924c9de2d56a00ef4e3d9de4d'  // Mainnet BWS token
            : ''),
        treasury: getEnvVar('REACT_APP_TREASURY_ADDRESS', ''),
        vault: getEnvVar('REACT_APP_VAULT_ADDRESS', ''),
        auditTrail: getEnvVar('REACT_APP_AUDIT_TRAIL_ADDRESS', ''),
      },
    },

    tables: {
      prefix: tablePrefix,
      tradingHistory: `${tablePrefix}-trading-history`,
      marketData: `${tablePrefix}-market-data`,
      revenueRecords: `${tablePrefix}-revenue-records`,
      strategyParameters: `${tablePrefix}-strategy-parameters`,
      performanceMetrics: `${tablePrefix}-performance-metrics`,
      threatIntelligence: `${tablePrefix}-threat-intelligence`,
      systemConfiguration: `${tablePrefix}-system-configuration`,
    },

    features: {
      realTimeUpdates: getEnvBool('REACT_APP_FEATURE_REAL_TIME_UPDATES', true),
      advancedCharts: getEnvBool('REACT_APP_FEATURE_ADVANCED_CHARTS', true),
      logStreaming: getEnvBool('REACT_APP_FEATURE_LOG_STREAMING', true),
      exportData: getEnvBool('REACT_APP_FEATURE_EXPORT_DATA', true),
      mockDataFallback: false, // NEVER use mock data - always require real connectivity
    },

    debug: {
      apiCalls: getEnvBool('REACT_APP_DEBUG_API_CALLS', isLocalStack),
      showDevTools: getEnvBool('REACT_APP_SHOW_DEV_TOOLS', isLocalStack),
    },

    intervals: {
      fast: getEnvNumber('REACT_APP_REFRESH_INTERVAL_FAST', 60000),    // Very conservative: 1 minute
      medium: getEnvNumber('REACT_APP_REFRESH_INTERVAL_MEDIUM', 120000), // Very conservative: 2 minutes
      slow: getEnvNumber('REACT_APP_REFRESH_INTERVAL_SLOW', 300000),    // Very conservative: 5 minutes
    },
  };
};

// Global configuration instance
export const config: AppConfig = createConfig();

// Environment helpers
export const isDevelopment = (): boolean => config.environment === 'local' || config.environment === 'development';
export const isProduction = (): boolean => config.environment === 'production';

// API URL helpers
export const getApiUrl = (path: string): string => {
  const baseUrl = config.api.baseUrl;
  // Handle undefined or null path
  if (!path) {
    return baseUrl;
  }
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

// LocalStack API URL helper
export const getLocalStackApiUrl = (service: string, path: string): string => {
  if (!config.localstack.enabled) {
    throw new Error('LocalStack is not enabled');
  }
  
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${config.localstack.endpoint}${cleanPath}`;
};

// DynamoDB API URL helper (for direct API calls)
export const getDynamoDbApiUrl = (action: string): string => {
  if (config.localstack.enabled) {
    return config.localstack.endpoint;
  }
  return `https://dynamodb.${config.aws.region}.amazonaws.com/`;
};

// WebSocket URL helper
export const getWebSocketUrl = (): string => {
  if (config.localstack.enabled) {
    // LocalStack doesn't support WebSockets, so we'll use polling instead
    return '';
  }
  
  const wsProtocol = config.api.baseUrl.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = config.api.baseUrl.replace(/^https?/, wsProtocol);
  return `${baseUrl}/ws`;
};

// Table name helpers
export const getTableName = (tableName: keyof AppConfig['tables']): string => {
  if (tableName === 'prefix') {
    return config.tables.prefix;
  }
  return config.tables[tableName];
};

// Debug logging
export const debugLog = (message: string, data?: any): void => {
  if (config.debug.apiCalls) {
    console.log(`[BWS Debug] ${message}`, data || '');
  }
};

// Configuration validation
export const validateConfig = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required configuration
  if (!config.api.baseUrl) {
    errors.push('API base URL is required');
  }

  if (!config.aws.region) {
    errors.push('AWS region is required');
  }

  if (config.localstack.enabled) {
    if (!config.localstack.endpoint) {
      errors.push('LocalStack endpoint is required when LocalStack is enabled');
    }
    
    if (config.localstack.port <= 0 || config.localstack.port > 65535) {
      errors.push('LocalStack port must be a valid port number');
    }
  }

  // Blockchain configuration
  if (!config.blockchain.contracts.bwsToken) {
    errors.push('BWS token address is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// Export default configuration
export default config;